import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const PaymentModal34 = () => {
  const {
    showPaymentModal,
    setShowPaymentModal,
    paymentProject,
    setProjects,
    showNotification
  } = useAppContext();

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  if (!showPaymentModal || !paymentProject) return null;
  
  // Only handle Stage 3 & 4 projects
  if (paymentProject.stage < 3) return null;

  // Determine payment amount and type - SYNCHRONIZED with manual inputs
  const getPaymentDetails = () => {
    // PRIORITY 1: Check for manual payment request from admin
    if (paymentProject.paymentRequired && paymentProject.paymentAmount) {
      return {
        amount: parseFloat(paymentProject.paymentAmount),
        description: paymentProject.paymentDescription || 'Payment requested by planAIdesign',
        type: 'requested'
      };
    }
    
    // PRIORITY 2: Initial payment after quote approval
    if ((paymentProject.status === 'stage-3-approved' || paymentProject.status === 'stage-4-approved') &&
        (!paymentProject.paymentsMade || paymentProject.paymentsMade === 0)) {
      // Get quote amount from deliverables if available
      const quoteDoc = paymentProject.deliverables?.find(d => d.type === 'quote-document');
      const quoteAmount = quoteDoc?.quoteAmount;
      
      // If admin set a specific initial payment amount
      if (paymentProject.initialPaymentAmount) {
        return {
          amount: parseFloat(paymentProject.initialPaymentAmount),
          description: 'Initial payment for project commencement',
          type: 'initial'
        };
      }
      
      // Otherwise use quote amount or default
      return {
        amount: quoteAmount || 5000,
        description: 'Initial payment for project commencement',
        type: 'initial'
      };
    }
    
    // PRIORITY 3: Revision payments
    if (paymentProject.revisionFee) {
      return {
        amount: paymentProject.revisionFee,
        description: `Revision payment for ${paymentProject.documentTypeLabel || 'document'}`,
        type: 'revision'
      };
    }
    
    // Default fallback - should not reach here in normal flow
    return {
      amount: 100,
      description: 'Payment',
      type: 'general'
    };
  };

  const paymentDetails = getPaymentDetails();

  const handlePayment = async () => {
    if (!acceptedTerms) {
      showNotification("Terms Required", "Please accept the terms and conditions", "warning");
      return;
    }

    setIsProcessing(true);

    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      // Create updated project data with proper payment tracking
      const paymentNumber = (paymentProject.paymentsMade || 0) + 1;
      const timestamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
      
      // Build the payment confirmation message using ACTUAL payment details
      const getPaymentConfirmationMessage = () => {
        const amount = paymentDetails.amount;
        
        // For manual payment requests
        if (paymentDetails.type === 'requested') {
          return `Thank you for payment ${paymentNumber} of $${amount}. ${
            paymentProject.paymentDescription ? 
            `${paymentProject.paymentDescription} has been received.` : 
            'Payment has been received and work will continue as scheduled.'
          }`;
        }
        
        // Initial payments
        if (paymentDetails.type === 'initial') {
          if (paymentProject.stage === 3) {
            return `Initial payment received. Thank you. Your Stage 3 authority approval documentation will commence shortly and you will be contacted by our team within 1-2 business days.`;
          } else if (paymentProject.stage === 4) {
            return `Initial payment received. Thank you. Your Stage 4 construction documentation will commence shortly and you will be contacted by our team within 1-2 business days.`;
          }
        }
        
        // Revision payment
        if (paymentDetails.type === 'revision') {
          return `Thank you for the revision payment of $${amount}. We will proceed with the requested revisions for ${paymentProject.documentTypeLabel || 'your documents'}.`;
        }
        
        // Default
        return `Thank you for payment ${paymentNumber} of $${amount}. Your project continues to progress.`;
      };
      
      const updatedProject = {
        ...paymentProject,
        paymentsMade: paymentNumber,
        // Clear payment requirements regardless of type
        paymentRequired: false,
        paymentAmount: null,
        paymentDescription: null,
        paymentHistory: [
          ...(paymentProject.paymentHistory || []),
          {
            amount: paymentDetails.amount,
            date: new Date().toISOString(),
            type: paymentDetails.type,
            method: paymentMethod,
            description: paymentDetails.description
          }
        ],
        communications: [
          ...(paymentProject.communications || []),
          {
            type: 'client',
            sender: paymentProject.client,
            message: `Payment ${paymentNumber} of $${paymentDetails.amount} made successfully.`,
            timestamp: timestamp
          },
          {
            type: 'architect',
            sender: 'planAIdesign',
            message: getPaymentConfirmationMessage(),
            timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
          }
        ]
      };

      // Update status for initial payments
      if (paymentDetails.type === 'initial') {
        if (paymentProject.status === 'stage-3-approved') {
          updatedProject.status = 'in-progress';
        } else if (paymentProject.status === 'stage-4-approved') {
          updatedProject.status = 'in-progress';
        }
      }

      console.log('Processing payment with updated project:', updatedProject);
      
      // Update local state FIRST for immediate UI response
      setProjects(prev => prev.map(p => 
        p.id === updatedProject.id ? updatedProject : p
      ));
      
      // Then save to Firebase
      try {
        await setDoc(doc(db, 'projects', updatedProject.id), updatedProject);
        console.log('Firebase save successful for project:', updatedProject.id);
      } catch (firebaseError) {
        console.error('Firebase save failed:', firebaseError);
        showNotification(
          'Database Sync Issue',
          'Payment processed but may not have saved to database. Please refresh the page.',
          'warning'
        );
      }

      setIsProcessing(false);
      showNotification(
        'Payment Successful',
        `Payment of $${paymentDetails.amount} has been processed successfully.`,
        'success'
      );
      
      // Close modal after success
      handleClose();
      
    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessing(false);
      showNotification(
        'Payment Error',
        'There was an error processing your payment. Please try again.',
        'error'
      );
    }
  };

  const handleClose = () => {
    setShowPaymentModal(false);
    setAcceptedTerms(false);
    setPaymentMethod('card');
  };

  // Get stage description for display
  const getStageDescription = () => {
    if (paymentProject.stage === 3) {
      return 'Stage 3 - Authority Approval Documentation';
    } else if (paymentProject.stage === 4) {
      return 'Stage 4 - Construction Documentation';
    }
    return `Stage ${paymentProject.stage}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Payment Details</h2>
        
        <div className="space-y-4">
          {/* Payment Information - Shows ACTUAL amounts */}
          <div className="bg-gray-50 p-4 rounded">
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Project:</span>
              <span className="text-right">{paymentProject.name}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Stage:</span>
              <span className="text-sm">{getStageDescription()}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Description:</span>
              <span className="text-sm text-right">{paymentDetails.description}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Amount:</span>
              <span className="text-xl font-bold text-green-600">
                ${paymentDetails.amount}
              </span>
            </div>
          </div>

          {/* Payment Progress Indicator */}
          {paymentProject.paymentsMade > 0 && (
            <div className="bg-blue-50 p-3 rounded">
              <p className="text-sm text-blue-800">
                <strong>Payment Progress:</strong> This will be payment {paymentProject.paymentsMade + 1} for this project.
              </p>
            </div>
          )}

          {/* Show if this is a manual payment request */}
          {paymentDetails.type === 'requested' && paymentProject.paymentDescription && (
            <div className="bg-yellow-50 p-3 rounded">
              <p className="text-sm text-yellow-800">
                <strong>Payment Request from planAIdesign:</strong><br/>
                {paymentProject.paymentDescription}
              </p>
            </div>
          )}

          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Payment Method</label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Credit/Debit Card</div>
                  <div className="text-xs text-gray-600">Secure payment via card</div>
                </div>
              </label>
              <label className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="bank"
                  checked={paymentMethod === 'bank'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Bank Transfer</div>
                  <div className="text-xs text-gray-600">Direct bank transfer</div>
                </div>
              </label>
            </div>
          </div>

          {/* Test Mode Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Test Mode</p>
                <p>This is a simulated payment for testing. In production, this would integrate with a real payment processor.</p>
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-2">Payment Terms</h3>
            <div className="text-xs text-gray-600 space-y-1 mb-3">
              <p>• Payments are processed according to the schedule agreed with planAIdesign.</p>
              <p>• Manual payment amounts are set by planAIdesign based on project requirements.</p>
              <p>• Progress payments may be required at various project milestones.</p>
              <p>• Additional payments may be required for revisions or additional services.</p>
              <p>• Refunds: Services are non-refundable once work has commenced.</p>
              <p>• Your statutory rights under the Australian Consumer Law are not affected.</p>
            </div>

            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5"
                disabled={isProcessing}
              />
              <span className="text-xs text-gray-700">
                I have read and accept the payment terms and conditions for Stage {paymentProject.stage} services.
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600 disabled:opacity-50"
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={isProcessing || !acceptedTerms}
            className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Processing...' : `Pay $${paymentDetails.amount}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal34;